import { useCallback, useEffect, useRef } from 'react'
import { captureException } from '@sentry/react'
import { RemoteAudioTrack, Track } from 'livekit-client'
import { logger as rootLogger } from '../services/Logger'

export interface AudioTrackProps2 {
    id: string
    track: Track
    isLocal: boolean
    sinkId: string
}

const log = rootLogger.createChildLogger({ name: 'ModifiedLiveKitAudioRenderer' })

export const ModifiedLiveKitAudioRenderer = ({ id, track, isLocal, sinkId }: AudioTrackProps2) => {
    const audioEl = useRef<HTMLAudioElement>()

    const setSinkId = useCallback(async (sinkId: string | null) => {
        log.info('setSinkId', `(id=${id}) calling setSinkId(${sinkId}).`)
        if (sinkId != null) {
            // set to default, then set the sinkId. Courtesy to
            //   <github.com/nwjs/nw.js/issues/4340>

            try {
                if (sinkId === 'default' || sinkId === '_default') {
                    log.info('setSinkId', `(id=${id}) Setting setSinkId='' for system default.`)

                    // @ts-ignore
                    audioEl.current.setSinkId('')
                    return
                }

                // NOTE: may have become unncessary with Electron update.
                //     log.info('setSinkId', `(id=${id}) Setting setSinkId=default as preflight.`)
                //     @ts-ignore
                //     await audioEl.current.setSinkId('default')
                //     log.verbose('setSinkId', `(id=${id}) Setting setSinkId=default success.`)

                log.info('setSinkId', `(id=${id}) Setting setSinkId=${sinkId}.`)
                // @ts-ignore
                await audioEl.current.setSinkId(sinkId)
                log.info('setSinkId', `(id=${id}) Success, audio sinkId=${sinkId}.`)
            } catch (e) {
                const ee = (e as any) || {}
                let errorMessage = e
                if (ee.name === 'SecurityError') {
                    errorMessage = `You need to use HTTPS for selecting audio output device: ${e}`
                } else {
                    log.error('setSinkId', `(id=${id}) Failed to setSinkId for sinkId=${sinkId}.`, { e })
                }
            }
        }
    }, [])

    useEffect(() => {
        if (isLocal) {
            // don't play own audio
            return
        }

        try {
            // `track.attach()` throws an exception if volume out of range (0, 1).
            if (track instanceof RemoteAudioTrack) {
                const volume = (track as RemoteAudioTrack).getVolume()
                if (volume > 1.0) {
                    ;(track as RemoteAudioTrack).setVolume(1.0)
                } else if (volume < 0.0) {
                    ;(track as RemoteAudioTrack).setVolume(0.0)
                }
            }

            audioEl.current = track.attach()
            if (track.sid) {
                audioEl.current.setAttribute('data-audio-track-id', track.sid)
            }

            // fix: leaking audio to default output device on creation
            audioEl.current.muted = true

            setSinkId(sinkId).then(() => {
                setTimeout(() => {
                    if (audioEl.current) {
                        audioEl.current.muted = false
                    }
                }, 100)
            })
        } catch (e) {
            log.error('effect', `(id=${id}) caught exception.`, { e })
            captureException(e)
        }

        return () => {
            try {
                log.info('effect', `(id=${id}) detaching.`)
                track.detach().forEach((el) => el.remove())
            } catch (e) {
                captureException(e)
            }
        }
    }, [track, isLocal, sinkId, setSinkId])

    // TODO: allow set sink id
    return null
}
